import { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Input from '../../../components/ui/Input';
import Button from '../../../components/ui/Button';


const SecuritySection = () => {
  const [passwordForm, setPasswordForm] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });
  const [passwordErrors, setPasswordErrors] = useState({});
  const [showPasswords, setShowPasswords] = useState({
    current: false,
    new: false,
    confirm: false
  });
  const [twoFactorEnabled, setTwoFactorEnabled] = useState(false);
  const [showTwoFactorSetup, setShowTwoFactorSetup] = useState(false);

  const activeSessions = [
    {
      id: 1,
      device: "Chrome on Windows",
      location: "New York, USA",
      ipAddress: "192.168.1.1",
      lastActive: "2025-12-08 15:45:00",
      current: true
    },
    {
      id: 2,
      device: "Safari on iPhone",
      location: "New York, USA",
      ipAddress: "192.168.1.2",
      lastActive: "2025-12-08 12:30:00",
      current: false
    },
    {
      id: 3,
      device: "Firefox on MacOS",
      location: "Boston, USA",
      ipAddress: "192.168.1.3",
      lastActive: "2025-12-07 18:20:00",
      current: false
    }
  ];

  const handlePasswordChange = (e) => {
    const { name, value } = e?.target;
    setPasswordForm(prev => ({ ...prev, [name]: value }));
    if (passwordErrors?.[name]) {
      setPasswordErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const validatePasswordForm = () => {
    const errors = {};
    if (!passwordForm?.currentPassword) errors.currentPassword = "Current password is required";
    if (!passwordForm?.newPassword) errors.newPassword = "New password is required";
    if (passwordForm?.newPassword && passwordForm?.newPassword?.length < 8) {
      errors.newPassword = "Password must be at least 8 characters";
    }
    if (passwordForm?.newPassword !== passwordForm?.confirmPassword) {
      errors.confirmPassword = "Passwords do not match";
    }
    setPasswordErrors(errors);
    return Object.keys(errors)?.length === 0;
  };

  const handlePasswordSubmit = () => {
    if (validatePasswordForm()) {
      setPasswordForm({ currentPassword: '', newPassword: '', confirmPassword: '' });
    }
  };

  const handleLogoutSession = (sessionId) => {
    console.log('Logging out session:', sessionId);
  };

  const formatLastActive = (timestamp) => {
    const date = new Date(timestamp);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    
    if (diffMins < 1) return "Active now";
    if (diffMins < 60) return `${diffMins} minutes ago`;
    if (diffMins < 1440) return `${Math.floor(diffMins / 60)} hours ago`;
    return date?.toLocaleDateString();
  };

  return (
    <div className="space-y-6">
      <div className="bg-card rounded-lg border border-border p-6">
        <div className="mb-6">
          <h2 className="text-xl font-semibold text-foreground">Change Password</h2>
          <p className="text-sm text-muted-foreground mt-1">
            Update your password to keep your account secure
          </p>
        </div>

        <div className="space-y-4 max-w-md">
          <div className="relative">
            <Input
              label="Current Password"
              type={showPasswords?.current ? "text" : "password"}
              name="currentPassword"
              value={passwordForm?.currentPassword}
              onChange={handlePasswordChange}
              error={passwordErrors?.currentPassword}
              placeholder="Enter current password"
              required
            />
            <button
              type="button"
              onClick={() => setShowPasswords(prev => ({ ...prev, current: !prev?.current }))}
              className="absolute right-3 top-9 text-muted-foreground hover:text-foreground"
            >
              <Icon name={showPasswords?.current ? "EyeOff" : "Eye"} size={18} />
            </button>
          </div>

          <div className="relative">
            <Input
              label="New Password"
              type={showPasswords?.new ? "text" : "password"}
              name="newPassword"
              value={passwordForm?.newPassword}
              onChange={handlePasswordChange}
              error={passwordErrors?.newPassword}
              placeholder="Enter new password"
              required
              description="Must be at least 8 characters"
            />
            <button
              type="button"
              onClick={() => setShowPasswords(prev => ({ ...prev, new: !prev?.new }))}
              className="absolute right-3 top-9 text-muted-foreground hover:text-foreground"
            >
              <Icon name={showPasswords?.new ? "EyeOff" : "Eye"} size={18} />
            </button>
          </div>

          <div className="relative">
            <Input
              label="Confirm New Password"
              type={showPasswords?.confirm ? "text" : "password"}
              name="confirmPassword"
              value={passwordForm?.confirmPassword}
              onChange={handlePasswordChange}
              error={passwordErrors?.confirmPassword}
              placeholder="Confirm new password"
              required
            />
            <button
              type="button"
              onClick={() => setShowPasswords(prev => ({ ...prev, confirm: !prev?.confirm }))}
              className="absolute right-3 top-9 text-muted-foreground hover:text-foreground"
            >
              <Icon name={showPasswords?.confirm ? "EyeOff" : "Eye"} size={18} />
            </button>
          </div>

          <Button variant="default" onClick={handlePasswordSubmit} className="mt-2">
            Update Password
          </Button>
        </div>
      </div>
      <div className="bg-card rounded-lg border border-border p-6">
        <div className="mb-6">
          <h2 className="text-xl font-semibold text-foreground">Two-Factor Authentication</h2>
          <p className="text-sm text-muted-foreground mt-1">
            Add an extra layer of security to your account
          </p>
        </div>

        <div className="flex items-start gap-4 p-4 bg-muted/50 rounded-lg border border-border">
          <div className="w-12 h-12 rounded-lg bg-primary/10 flex items-center justify-center flex-shrink-0">
            <Icon name="Shield" size={24} className="text-primary" />
          </div>
          <div className="flex-1">
            <div className="flex items-center gap-2 mb-2">
              <h3 className="text-base font-semibold text-foreground">
                Two-Factor Authentication
              </h3>
              <span className={`px-2 py-0.5 rounded text-xs font-medium ${
                twoFactorEnabled 
                  ? 'bg-success/10 text-success' :'bg-muted text-muted-foreground'
              }`}>
                {twoFactorEnabled ? 'Enabled' : 'Disabled'}
              </span>
            </div>
            <p className="text-sm text-muted-foreground mb-4">
              Protect your account with an additional verification step during login
            </p>
            <Button 
              variant={twoFactorEnabled ? "outline" : "default"}
              size="sm"
              iconName={twoFactorEnabled ? "X" : "Shield"}
              iconPosition="left"
              onClick={() => {
                if (!twoFactorEnabled) {
                  setShowTwoFactorSetup(true);
                } else {
                  setTwoFactorEnabled(false);
                }
              }}
            >
              {twoFactorEnabled ? 'Disable 2FA' : 'Enable 2FA'}
            </Button>
          </div>
        </div>

        {showTwoFactorSetup && !twoFactorEnabled && (
          <div className="mt-4 p-4 border border-border rounded-lg">
            <h4 className="text-sm font-semibold text-foreground mb-3">
              Setup Two-Factor Authentication
            </h4>
            <ol className="space-y-2 text-sm text-muted-foreground mb-4">
              <li className="flex gap-2">
                <span className="font-semibold text-foreground">1.</span>
                <span>Download an authenticator app (Google Authenticator, Authy, etc.)</span>
              </li>
              <li className="flex gap-2">
                <span className="font-semibold text-foreground">2.</span>
                <span>Scan the QR code with your authenticator app</span>
              </li>
              <li className="flex gap-2">
                <span className="font-semibold text-foreground">3.</span>
                <span>Enter the 6-digit code from your app to verify</span>
              </li>
            </ol>
            <div className="flex gap-3">
              <Button 
                variant="default" 
                size="sm"
                onClick={() => {
                  setTwoFactorEnabled(true);
                  setShowTwoFactorSetup(false);
                }}
              >
                Complete Setup
              </Button>
              <Button 
                variant="outline" 
                size="sm"
                onClick={() => setShowTwoFactorSetup(false)}
              >
                Cancel
              </Button>
            </div>
          </div>
        )}
      </div>
      <div className="bg-card rounded-lg border border-border p-6">
        <div className="mb-6">
          <h2 className="text-xl font-semibold text-foreground">Active Sessions</h2>
          <p className="text-sm text-muted-foreground mt-1">
            Manage devices where you're currently logged in
          </p>
        </div>

        <div className="space-y-3">
          {activeSessions?.map((session) => (
            <div 
              key={session?.id}
              className="flex items-start justify-between gap-4 p-4 border border-border rounded-lg hover:border-primary/50 transition-colors"
            >
              <div className="flex items-start gap-3 flex-1">
                <div className="w-10 h-10 rounded-lg bg-muted flex items-center justify-center flex-shrink-0">
                  <Icon name="Monitor" size={20} />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1">
                    <h3 className="text-sm font-semibold text-foreground">
                      {session?.device}
                    </h3>
                    {session?.current && (
                      <span className="px-2 py-0.5 rounded text-xs font-medium bg-success/10 text-success">
                        Current
                      </span>
                    )}
                  </div>
                  <div className="space-y-0.5 text-xs text-muted-foreground">
                    <p className="flex items-center gap-1">
                      <Icon name="MapPin" size={12} />
                      {session?.location}
                    </p>
                    <p className="flex items-center gap-1">
                      <Icon name="Globe" size={12} />
                      {session?.ipAddress}
                    </p>
                    <p className="flex items-center gap-1">
                      <Icon name="Clock" size={12} />
                      {formatLastActive(session?.lastActive)}
                    </p>
                  </div>
                </div>
              </div>
              {!session?.current && (
                <Button 
                  variant="outline" 
                  size="sm"
                  onClick={() => handleLogoutSession(session?.id)}
                >
                  Logout
                </Button>
              )}
            </div>
          ))}
        </div>

        <div className="mt-4">
          <Button variant="destructive" size="sm" iconName="LogOut" iconPosition="left">
            Logout All Other Sessions
          </Button>
        </div>
      </div>
    </div>
  );
};

export default SecuritySection;